import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, resolveImageUrl } from "../lib/api";

export default function Admin() {
  const [images, setImages] = useState([]);
  const [styles, setStyles] = useState([]);
  const [summary, setSummary] = useState(null);
  const [uploadStyle, setUploadStyle] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    const [imgs, sts, sum] = await Promise.all([api.admin.list(), api.listStyles(), api.admin.summary()]);
    setImages(imgs);
    setStyles(sts);
    setSummary(sum);
    setUploadStyle((prev) => prev || sts[0]?.slug || "");
  }, []);

  useEffect(() => {
    load().catch(() => toast.error("Failed to load admin data"));
  }, [load]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file || !uploadStyle) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("primary_style", uploadStyle);
      fd.append("secondary_weights_json", "{}");
      await api.admin.upload(fd);
      toast.success("Image uploaded");
      setFile(null);
      await load();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const updatePrimary = async (id, slug) => {
    try {
      await api.admin.update(id, { primary_style: slug, weights: { [slug]: 3.0 } });
      toast.success("Updated");
      await load();
    } catch {
      toast.error("Update failed");
    }
  };

  const toggleActive = async (img) => {
    try {
      await api.admin.update(img.id, { active: !img.active });
      await load();
    } catch {
      toast.error("Update failed");
    }
  };

  const shown = filter === "all" ? images : images.filter((i) => i.primary_style === filter);

  return (
    <div className="min-h-screen pb-24 grain" data-testid="admin-page">
      <div className="container-editorial pt-8 pb-6 flex justify-between items-center">
        <Link to="/" className="font-serif-display text-2xl md:text-3xl">
          Aesthos<span className="text-[color:var(--terracotta)]">.</span> <span className="text-[color:var(--ink-soft)] text-base ml-2">Admin</span>
        </Link>
        <Link to="/quiz" className="overline">↩ Back to quiz</Link>
      </div>
      <div className="hairline container-editorial" />

      {/* Summary */}
      <section className="container-editorial pt-10 pb-10">
        <p className="overline mb-4">Overview</p>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 md:gap-6">
          {summary?.styles?.map((s) => (
            <div key={s.slug} className="border border-[color:var(--border)] p-4">
              <p className="overline mb-1">{s.name}</p>
              <p className="font-serif-display text-3xl">
                {summary.image_counts_by_style[s.slug] ?? 0}
                <span className="text-[color:var(--ink-soft)] text-lg"> imgs</span>
              </p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-sm text-[color:var(--ink-soft)]" data-testid="admin-total-active">
          Total active images: {summary?.total_active_images ?? 0}
        </p>
      </section>

      {/* Upload */}
      <section className="container-editorial pb-10">
        <div className="hairline mb-8" />
        <p className="overline mb-4">Upload a new image</p>
        <form className="flex flex-col md:flex-row items-start md:items-end gap-4" onSubmit={handleUpload}>
          <div className="flex-1">
            <label className="overline block mb-2">Image file</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm"
              data-testid="admin-upload-file"
            />
          </div>
          <div>
            <label className="overline block mb-2">Primary style</label>
            <select
              value={uploadStyle}
              onChange={(e) => setUploadStyle(e.target.value)}
              className="bg-transparent border-b border-[color:var(--ink)] py-2 px-1 text-base"
              data-testid="admin-style-select"
            >
              {styles.map((s) => (
                <option key={s.slug} value={s.slug}>{s.name}</option>
              ))}
            </select>
          </div>
          <button className="btn-primary" type="submit" disabled={uploading || !file} data-testid="admin-upload-button">
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </form>
      </section>

      {/* Filter */}
      <section className="container-editorial pb-6">
        <div className="hairline mb-6" />
        <div className="flex flex-wrap gap-2 items-center">
          <span className="overline mr-3">Filter</span>
          <button
            onClick={() => setFilter("all")}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest border ${filter === "all" ? "bg-[color:var(--ink)] text-[color:var(--bg)] border-[color:var(--ink)]" : "border-[color:var(--border)] text-[color:var(--ink-soft)]"}`}
          >
            All
          </button>
          {styles.map((s) => (
            <button
              key={s.slug}
              onClick={() => setFilter(s.slug)}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest border ${filter === s.slug ? "bg-[color:var(--ink)] text-[color:var(--bg)] border-[color:var(--ink)]" : "border-[color:var(--border)] text-[color:var(--ink-soft)]"}`}
            >
              {s.name}
            </button>
          ))}
        </div>
      </section>

      {/* Image grid */}
      <section className="container-editorial">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="admin-image-grid">
          {shown.map((img) => (
            <div key={img.id} className={`border border-[color:var(--border)] ${!img.active ? "opacity-50" : ""}`}>
              <div className="aspect-[4/5] bg-[color:var(--border)] overflow-hidden">
                <img
                  src={resolveImageUrl(img)}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-3 space-y-2">
                <select
                  value={img.primary_style}
                  onChange={(e) => updatePrimary(img.id, e.target.value)}
                  className="w-full bg-transparent border-b border-[color:var(--border)] text-sm py-1"
                  data-testid="admin-image-style"
                >
                  {styles.map((s) => (
                    <option key={s.slug} value={s.slug}>{s.name}</option>
                  ))}
                </select>
                <div className="flex items-center justify-between text-xs">
                  <span className="overline" style={{ fontSize: "0.6rem" }}>
                    W: {Object.entries(img.weights || {}).map(([k, v]) => `${k}:${v}`).join(", ")}
                  </span>
                  <button
                    onClick={() => toggleActive(img)}
                    className="underline text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]"
                    data-testid="admin-toggle-active"
                  >
                    {img.active ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
